var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './vitesse-icon';
/**
 * <vitesse-button> — THE one reusable text button, as a Web Component so it works
 * inside shadow DOM (dialogs, other elements) where the global `.base-button` CSS
 * + the `button()` template helper can't reach. Same treatment as `.base-button`:
 * flat/transparent with a border, the text + border recolour on hover (and on the
 * toggled `active` state); the leading icon keeps its neutral colour so the accent
 * pops. One element, a few attributes — never hand-roll a button.
 *
 *   <vitesse-button color="blue" icon="plus" @click=${onAdd}>Add controller</vitesse-button>
 *   <vitesse-button color="red" size="sm" @click=${onDelete}>Delete</vitesse-button>
 *   <vitesse-button color="blue" filled @click=${onSave}>Save</vitesse-button>
 *
 * `filled` is the PRIMARY action — one per surface. Without it a button is an
 * outline that takes its colour on hover, which is what a toolbar of equal
 * options wants.
 *
 * The label is the default slot. Native click bubbles (composed) — add `@click`
 * on the host. `disabled` suppresses it.
 */
let VitesseButton = class VitesseButton extends LitElement {
    constructor() {
        super(...arguments);
        /** Highlight colour applied on hover + when `active`. */
        this.color = 'neutral';
        this.size = 'md';
        /** Leading lucide icon name (optional). */
        this.icon = '';
        /** Render as a filled primary action instead of the default outline. */
        this.filled = false;
        this.disabled = false;
        this.active = false;
    }
    static { this.styles = css `
    :host {
      display: inline-flex;
    }
    /* A host given a width passes it through — otherwise the button inside stays its own size and
       the host draws an empty box around it, which is two rectangles for one control. */
    button {
      width: 100%;
    }
    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: 30px;
      padding: 0 12px;
      border: 1px solid var(--border-color);
      border-radius: 6px;
      background: transparent;
      color: var(--text-secondary);
      font-size: 13px;
      font-weight: 500;
      font-family: inherit;
      cursor: pointer;
      white-space: nowrap;
      transition:
        color 0.15s ease,
        border-color 0.15s ease,
        background 0.15s ease;
    }
    /* The icon keeps its neutral colour through hover — only the text + border
       take the highlight, which makes the accent pop. */
    vitesse-icon {
      color: var(--text-secondary);
      flex-shrink: 0;
    }
    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* ── highlight colour: hover (and toggled .active) recolour ── */
    :host([color='blue']) button:hover:not(:disabled),
    :host([color='blue'][active]) button {
      color: var(--blue);
      border-color: var(--blue);
    }
    :host([color='red']) button:hover:not(:disabled),
    :host([color='red'][active]) button {
      color: var(--red);
      border-color: var(--red);
    }
    :host([color='green']) button:hover:not(:disabled),
    :host([color='green'][active]) button {
      color: var(--green);
      border-color: var(--green);
    }
    :host([color='yellow']) button:hover:not(:disabled),
    :host([color='yellow'][active]) button {
      color: var(--yellow);
      border-color: var(--yellow);
    }
    :host([color='neutral']) button:hover:not(:disabled) {
      color: var(--text-primary);
      border-color: var(--text-secondary);
    }

    /* ── filled: the PRIMARY action ──
       The default button is an outline that only takes its colour on hover,
       which is right for a toolbar full of equal options and wrong for the one
       action a dialog exists to perform. Apps were hand-rolling this — three
       independently landed on the same fill, weight and brightness hover, which
       is what it should have been here. Geometry is inherited from the block
       above, so a filled button and an outline one still line up. */
    :host([filled]) button {
      color: var(--on-accent);
      border-color: transparent;
      font-weight: 600;
    }
    :host([filled]) button:hover:not(:disabled) {
      /* Brighten the fill rather than recolour text and border — on a filled
         surface those are already the accent's counterpart. */
      filter: brightness(1.08);
      color: var(--on-accent);
    }
    :host([filled][color='blue']) button { background: var(--blue); }
    :host([filled][color='red']) button { background: var(--red); }
    :host([filled][color='green']) button { background: var(--green); }
    :host([filled][color='yellow']) button { background: var(--yellow); }
    :host([filled][color='neutral']) button {
      background: var(--bg-tertiary);
      color: var(--text-primary);
    }
    :host([filled]) vitesse-icon { color: inherit; }

    /* ── size scale — lg = page actions, md (default) = toolbars, sm = compact ── */
    :host([size='sm']) button {
      height: 26px;
      padding: 0 9px;
      font-size: 12px;
      gap: 5px;
    }
    :host([size='lg']) button {
      height: 36px;
      padding: 0 16px;
      font-size: 14px;
      gap: 8px;
    }
  `; }
    iconSize() {
        return this.size === 'sm' ? 13 : this.size === 'lg' ? 16 : 14;
    }
    render() {
        return html `<button ?disabled=${this.disabled}>
      ${this.icon ? html `<vitesse-icon name=${this.icon} size=${this.iconSize()}></vitesse-icon>` : nothing}<slot></slot>
    </button>`;
    }
};
__decorate([
    property({ reflect: true })
], VitesseButton.prototype, "color", void 0);
__decorate([
    property({ reflect: true })
], VitesseButton.prototype, "size", void 0);
__decorate([
    property()
], VitesseButton.prototype, "icon", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseButton.prototype, "filled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VitesseButton.prototype, "active", void 0);
VitesseButton = __decorate([
    customElement('vitesse-button')
], VitesseButton);
export { VitesseButton };
//# sourceMappingURL=vitesse-button.js.map